import { useCallback, useEffect, useRef, useState } from "react";

interface UseFakeProgressProps {
  autoStart?: boolean;
  speed?: "slow" | "normal" | "fast";
  enabled?: boolean;
}

const speedMap = {
  slow: 500,
  normal: 200,
  fast: 100
} as const;

export function useFakeProgress({
  autoStart = true,
  speed = "normal",
  enabled = true
}: UseFakeProgressProps = {}) {
  const [value, setValue] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateProgress = useCallback(() => {
    setValue((prev) => {
      if (prev >= 99) return 99;
      const next = prev + (100 - prev) * 0.03;
      return Math.min(next, 99);
    });
  }, []);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (enabled) {
      setValue(0);
      if (autoStart) {
        intervalRef.current = setInterval(updateProgress, speedMap[speed]);
      }
    } else {
      setValue(100);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, autoStart, speed, updateProgress]);

  const start = useCallback(() => {
    if (intervalRef.current || !enabled) return;
    intervalRef.current = setInterval(updateProgress, speedMap[speed]);
  }, [speed, enabled, updateProgress]);

  const done = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setValue(100);
  }, []);

  return { value, start, done };
}
